import React, { useState, useEffect, useContext } from "react";
import styles from "./CompanyInfo.module.scss";
import useForm from "../../hooks/useForm";
import _ from "lodash";
import { isDate } from "date-fns";
import { useMutation } from "@apollo/client";
import gql from "graphql-tag";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import { FetchContext } from "../../context/fetchContext";
import { SnackbarContext } from "../../context/snackbarContext";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  IconButton,
  List,
  ListItem,
  ListItemText,
} from "@material-ui/core";
import { DatePicker } from "@material-ui/pickers";
import VisibilityIcon from "@material-ui/icons/Visibility";
import VisibilityOffIcon from "@material-ui/icons/VisibilityOff";
import { getCoreDisplayName } from "../../utils/misc";

const initErrorForm = {
  name: ["required"],
};

const EditDialog = ({ id, open, handleClose, initForm, minYear }) => {
  const { requestFetch } = useContext(FetchContext);
  const { snack } = useContext(SnackbarContext);
  const [listKey, setListKey] = useState(false);
  const [updateOrg] = useMutation(UPDATE_ORG);

  const { form, formErrors, handleChange, handleChangeDate, resetForm, validateForm } = useForm({
    initForm,
    initErrorForm,
  });

  const handleDragEndOrg = ({ draggableId, destination, source }) => {
    if (!destination) return;
    setListKey(!listKey);

    const indexOfName = form.viewPreferences.findIndex((obj) => obj.name === draggableId);
    const clonedForm = _.cloneDeep(form);

    clonedForm.viewPreferences[indexOfName] = { ...clonedForm.viewPreferences[indexOfName], position: destination.index };

    if (source.index < destination.index) {
      const toDecrement = clonedForm.viewPreferences.filter(
        ({ name, position }) =>
          position <= destination.index && name !== clonedForm.viewPreferences[indexOfName].name && position > source.index
      );

      toDecrement.forEach((obj) => obj.position--);
    } else if (source.index > destination.index) {
      const toIncrement = clonedForm.viewPreferences.filter(
        ({ name, position }) =>
          position >= destination.index && name !== clonedForm.viewPreferences[indexOfName].name && position < source.index
      );

      toIncrement.forEach((obj) => obj.position++);
    }

    resetForm(clonedForm);
  };

  const handleChangeHidden = (name, show) => () => {
    const indexOfName = form.viewPreferences.findIndex((obj) => obj.name === name);
    const clonedForm = _.cloneDeep(form);
    clonedForm.viewPreferences[indexOfName] = { ...clonedForm.viewPreferences[indexOfName], show: !show };
    resetForm(clonedForm);
  };

  const handleSubmitOrg = async () => {
    if (!validateForm()) return;

    const { name, fiscalYear, viewPreferences } = form;

    try {
      const ok = await updateOrg({
        variables: {
          id,
          name,
          fiscalYear: isDate(fiscalYear) ? fiscalYear : new Date(fiscalYear),
          viewPreferences,
        },
      });

      if (ok.data.updateOrganization) {
        snack("Updated organization details");
        requestFetch();
        handleClose();
      }
    } catch (err) {
      snack("Failed to update organization details", "error");
    }
  };

  useEffect(() => {
    resetForm(initForm);
  }, [open]);

  return (
    <>
      <Dialog
        open={Boolean(open.org)}
        fullWidth
        onClose={(event, reason) => {
          if (reason !== "backdropClick") {
            handleClose();
          }
        }}
      >
        <DialogTitle>Edit Organization</DialogTitle>
        <DialogContent>
          <TextField
            label="Organization Name"
            name="name"
            fullWidth
            margin="normal"
            variant="outlined"
            value={form.name || ""}
            onChange={handleChange}
            helperText={formErrors.name}
            error={Boolean(formErrors.name)}
          />
          <DatePicker
            fullWidth
            autoOk
            variant="inline"
            inputVariant="outlined"
            format="dd/MM/yyyy"
            label="Fiscal Year End"
            margin="normal"
            value={form.fiscalYear || ""}
            onChange={handleChangeDate("fiscalYear")}
            minDate={minYear}
          />
          <DragDropContext onDragEnd={handleDragEndOrg}>
            <Droppable droppableId={"0"}>
              {(provided, snapshot) => {
                return (
                  <div {...provided.droppableProps} ref={provided.innerRef}>
                    <List>
                      {form.viewPreferences &&
                        _.sortBy(form.viewPreferences, "position").map(({ name, position, show }) => {
                          const displayName = getCoreDisplayName(name);
                          return (
                            <Draggable draggableId={name} index={position} key={`${position}${listKey.toString()}`}>
                              {(provided, snapshot) => {
                                const isCore = name !== "projects" ? "Core " : "";
                                return (
                                  <ListItem divider {...provided.draggableProps} {...provided.dragHandleProps} ref={provided.innerRef}>
                                    <ListItemText>
                                      <div className={styles.flexBetween}>
                                        {isCore}
                                        {_.startCase(displayName)}
                                        <IconButton onClick={handleChangeHidden(name, show)}>
                                          {show ? <VisibilityIcon /> : <VisibilityOffIcon />}
                                        </IconButton>
                                      </div>
                                    </ListItemText>
                                  </ListItem>
                                );
                              }}
                            </Draggable>
                          );
                        })}
                    </List>
                    {provided.placeholder}
                  </div>
                );
              }}
            </Droppable>
          </DragDropContext>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button color="primary" onClick={handleSubmitOrg} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default EditDialog;

const UPDATE_ORG = gql`
  mutation ($id: ID!, $name: String!, $fiscalYear: String!, $viewPreferences: [ViewPreferencesInput!]) {
    updateOrganization(id: $id, name: $name, fiscalYear: $fiscalYear, viewPreferences: $viewPreferences)
  }
`;
